import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { CONTACT } from '../mock';
import { track, trackLead } from '../lib/analytics';

const QUESTIONS = [
  {
    id: 'goal',
    title: 'Was ist dein Hauptziel?',
    subtitle: 'Wähle das aus, was am besten passt.',
    options: [
      { value: 'grades',    label: 'Noten verbessern',       emoji: '📈' },
      { value: 'exam',      label: 'Klausur / Abitur bestehen', emoji: '📚' },
      { value: 'confidence',label: 'Selbstvertrauen aufbauen', emoji: '💪' },
      { value: 'basics',    label: 'Grundlagen aufholen',    emoji: '🎯' },
    ],
  },
  {
    id: 'level',
    title: 'In welcher Klassenstufe bist du?',
    subtitle: 'Damit wir den passenden Tutor finden.',
    options: [
      { value: 'grund',  label: 'Grundschule',  emoji: '✏️' },
      { value: 'mittel', label: 'Mittelstufe (5–10)', emoji: '📖' },
      { value: 'ober',   label: 'Oberstufe (11–13)',  emoji: '🎓' },
      { value: 'uni',    label: 'Studium',      emoji: '🏛️' },
    ],
  },
  {
    id: 'freq',
    title: 'Wie oft brauchst du Unterstützung?',
    subtitle: 'Kein Problem, das lässt sich später anpassen.',
    options: [
      { value: 'once',    label: 'Einmalig (Klausur-Vorbereitung)', emoji: '⚡' },
      { value: 'weekly',  label: '1× pro Woche',    emoji: '📅' },
      { value: 'twice',   label: '2× pro Woche',    emoji: '🔥' },
      { value: 'intense', label: 'Täglich / Intensiv', emoji: '🚀' },
    ],
  },
];

function recommend(answers) {
  const { goal, level, freq } = answers;
  if (goal === 'exam' || freq === 'intense') {
    return {
      plan: '90-Minuten-Einheiten',
      price: 'Preis je nach Fach & Stufe – siehe Preisrechner',
      badge: 'Perfekt für Prüfungen',
      text: 'Wir empfehlen dir längere 90-Minuten-Einheiten für Klausur- und Prüfungsvorbereitung mit genug Zeit für Deep-Dives und Klausursimulationen.',
    };
  }
  if (freq === 'weekly' || freq === 'twice' || goal === 'grades' || goal === 'confidence') {
    return {
      plan: '60-Minuten-Einheiten',
      price: 'Preis je nach Fach & Stufe – siehe Preisrechner',
      badge: 'Beste Balance',
      text: 'Regelmäßige 60-Minuten-Einzelstunden mit strukturiertem Förderplan passen ideal zu deinem Ziel.',
    };
  }
  return {
    plan: 'Kostenloses Erstgespräch',
    price: 'Kostenlos',
    badge: 'Erster Schritt',
    text: 'Starte mit einer kostenlosen Bedarfsanalyse. Danach entscheidest du in Ruhe.',
  };
}

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(false);

  const q = QUESTIONS[step];
  const progress = Math.round(((step + (done ? 1 : 0)) / QUESTIONS.length) * 100);

  const choose = (val) => {
    const next = { ...answers, [q.id]: val };
    setAnswers(next);
    if (step + 1 < QUESTIONS.length) {
      setTimeout(() => setStep(step + 1), 220);
    } else {
      setTimeout(() => {
        setDone(true);
        const r = recommend(next);
        track('quiz_completed', { ...next, recommendation: r.plan });
        trackLead('quiz', { recommendation: r.plan, ...next });
      }, 260);
    }
  };

  const back = () => {
    if (done) { setDone(false); return; }
    if (step > 0) setStep(step - 1);
  };

  if (done) {
    const r = recommend(answers);
    return (
      <div className="hero-bg py-14 md:py-20">
        <div className="max-w-2xl mx-auto px-5 lg:px-8">
          <div className="card-soft p-8 md:p-10 text-center" style={{ animation: 'fadeUp 500ms ease both' }}>
            <div className="h-16 w-16 mx-auto rounded-full flex items-center justify-center shadow-md" style={{ background: 'linear-gradient(135deg, #4aa5c8, #2d7d9a)' }}>
              <CheckCircle2 className="h-8 w-8 text-white" />
            </div>
            <span className="inline-block mt-5 text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full text-white" style={{ background: '#3d94b8' }}>{r.badge}</span>
            <h1 className="mt-4 text-3xl md:text-4xl font-extrabold" style={{ color: '#1a3a5c' }}>Deine Empfehlung: <span style={{ color: '#3d94b8' }}>{r.plan}</span></h1>
            <p className="mt-2 text-lg font-semibold text-slate-700">{r.price === 'Kostenlos' ? r.price : <Link to="/#preise" className="hover:underline" style={{ color: '#2d7d9a' }}>{r.price}</Link>}</p>
            <p className="mt-4 text-slate-600 leading-relaxed">{r.text}</p>

            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/buchen">
                <Button className="btn-teal rounded-full h-12 px-6">
                  <Sparkles className="mr-2 h-4 w-4" /> Kostenloses Erstgespräch
                </Button>
              </Link>
              <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer">
                <Button className="btn-whatsapp rounded-full h-12 px-6">
                  <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
                </Button>
              </a>
            </div>

            <button onClick={back} className="mt-6 text-sm font-semibold hover:underline" style={{ color: '#2d7d9a' }}>
              ← Antworten anpassen
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hero-bg py-14 md:py-20">
      <div className="max-w-2xl mx-auto px-5 lg:px-8">
        <div className="text-center">
          <span className="font-semibold uppercase tracking-wider text-xs" style={{ color: '#3d94b8' }}>Bedarfsanalyse</span>
          <h1 className="mt-2 text-3xl md:text-4xl font-extrabold" style={{ color: '#1a3a5c' }}>3 Fragen zu deiner idealen Nachhilfe</h1>
        </div>

        {/* Progress bar */}
        <div className="mt-8 h-2 bg-white rounded-full overflow-hidden shadow-inner">
          <div
            className="h-full transition-all duration-500 rounded-full"
            style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #4aa5c8, #2d7d9a)' }}
          />
        </div>
        <div className="mt-2 text-xs text-slate-500 flex justify-between">
          <span>Frage {step + 1} von {QUESTIONS.length}</span>
          <span>{progress}%</span>
        </div>

        <div key={q.id} className="card-soft p-8 md:p-10 mt-6" style={{ animation: 'fadeUp 400ms ease both' }}>
          <h2 className="text-xl md:text-2xl font-extrabold" style={{ color: '#1a3a5c' }}>{q.title}</h2>
          <p className="mt-1 text-sm text-slate-500">{q.subtitle}</p>

          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            {q.options.map((o) => {
              const active = answers[q.id] === o.value;
              return (
                <button
                  key={o.value}
                  onClick={() => choose(o.value)}
                  className={`text-left p-4 rounded-2xl border-2 transition-all hover:-translate-y-0.5 hover:shadow-md ${active ? 'text-white' : 'bg-white border-slate-200 hover:border-teal-300'}`}
                  style={active ? { background: '#3d94b8', borderColor: '#3d94b8' } : {}}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{o.emoji}</span>
                    <span className={`font-semibold ${active ? 'text-white' : ''}`} style={!active ? { color: '#1a3a5c' } : {}}>{o.label}</span>
                  </div>
                </button>
              );
            })}
          </div>

          {step > 0 && (
            <button onClick={back} className="mt-6 inline-flex items-center gap-1 text-sm font-semibold hover:underline" style={{ color: '#2d7d9a' }}>
              <ArrowLeft className="h-4 w-4" /> Zurück
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
